import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { SerializableMessage } from '@/pages/AIChatCopilotKit/types';

/**
 * Redux slice for managing CopilotKit chat state
 *
 * This slice is specifically for CopilotKit-based chat (CHAT_NAMESPACES.GIGAWATT_COPILOTKIT)
 * and stores sanitized messages that are safe for Redux serialization.
 *
 * State structure:
 * {
 *   conversations: {
 *     [CHAT_NAMESPACES.GIGAWATT_COPILOTKIT]: { conversationId, messages, lastToolset (null until agent selects one), isCreatingNewConversation, needsHistoryRestore }
 *   }
 * }
 */

type CopilotKitConversationState = {
  conversationId: string;
  messages: SerializableMessage[];
  lastToolset: string | null; // Last selected toolset (e.g., 'application', 'asset'); null until agent selects one
  isCreatingNewConversation: boolean; // True while a new-chat API call is in-flight
  needsHistoryRestore: boolean; // True when conversationId was restored from localStorage and history must be fetched
};

type CopilotKitChatState = {
  conversations: {
    [namespaceKey: string]: CopilotKitConversationState;
  };
};

const initialState: CopilotKitChatState = {
  conversations: {},
};

/**
 * Helper to get or initialize a conversation by namespace key
 */
const getOrInitConversation = (
  state: CopilotKitChatState,
  namespaceKey: string
): CopilotKitConversationState => {
  if (!state.conversations[namespaceKey]) {
    state.conversations[namespaceKey] = {
      conversationId: '',
      messages: [],
      lastToolset: null, // No toolset selected; agent will call select_toolset when needed
      isCreatingNewConversation: false,
      needsHistoryRestore: false,
    };
  }
  return state.conversations[namespaceKey];
};

const copilotKitChatSlice = createSlice({
  name: 'copilotKitChat',
  initialState,
  reducers: {
    // Set the conversation ID for a specific namespace
    setConversationId: (
      state,
      action: PayloadAction<{ namespaceKey: string; conversationId: string }>
    ) => {
      const conv = getOrInitConversation(state, action.payload.namespaceKey);
      conv.conversationId = action.payload.conversationId;
    },

    // Set all messages for a conversation (used for syncing from CopilotKit)
    setMessages: (
      state,
      action: PayloadAction<{
        namespaceKey: string;
        messages: SerializableMessage[];
      }>
    ) => {
      const conv = getOrInitConversation(state, action.payload.namespaceKey);
      conv.messages = action.payload.messages;
    },

    // Set last toolset for a conversation
    setLastToolset: (
      state,
      action: PayloadAction<{ namespaceKey: string; toolset: string }>
    ) => {
      const conv = getOrInitConversation(state, action.payload.namespaceKey);
      conv.lastToolset = action.payload.toolset;
    },

    // Atomically swap conversationId and clear messages without passing through undefined.
    // Used by useNewChatAction for CopilotKit path — fires the API first, then commits.
    swapConversation: (
      state,
      action: PayloadAction<{ namespaceKey: string; conversationId: string }>
    ) => {
      const conv = getOrInitConversation(state, action.payload.namespaceKey);
      conv.conversationId = action.payload.conversationId;
      conv.messages = [];
      conv.needsHistoryRestore = false;
      conv.lastToolset = null; // reset so new conversation starts with no active toolset
    },

    // Restore a conversation from localStorage without creating a new one.
    // Sets needsHistoryRestore so the agent hook knows to fetch history from the backend.
    restoreConversation: (
      state,
      action: PayloadAction<{ namespaceKey: string; conversationId: string }>
    ) => {
      const conv = getOrInitConversation(state, action.payload.namespaceKey);
      conv.conversationId = action.payload.conversationId;
      conv.needsHistoryRestore = true;
    },

    // Reset conversationId to '' so CopilotKitProvider creates a fresh conversation.
    // Used when a stored conversationId is found to be invalid (e.g. expired / not found).
    clearConversation: (
      state,
      action: PayloadAction<{ namespaceKey: string }>
    ) => {
      const conv = getOrInitConversation(state, action.payload.namespaceKey);
      conv.conversationId = '';
      conv.messages = [];
      conv.needsHistoryRestore = false;
      conv.lastToolset = null; // reset so fresh conversation starts with no active toolset
    },

    // Clear the needsHistoryRestore flag once history has been fetched and applied.
    setNeedsHistoryRestore: (
      state,
      action: PayloadAction<{ namespaceKey: string; value: boolean }>
    ) => {
      const conv = getOrInitConversation(state, action.payload.namespaceKey);
      conv.needsHistoryRestore = action.payload.value;
    },

    // Set the isCreatingNewConversation flag for a namespace.
    // Driven by useNewChatAction to show a spinner immediately on button click,
    // before the RTK mutation's own isLoading can update and re-render.
    setCreatingNewConversation: (
      state,
      action: PayloadAction<{ namespaceKey: string; value: boolean }>
    ) => {
      const conv = getOrInitConversation(state, action.payload.namespaceKey);
      conv.isCreatingNewConversation = action.payload.value;
    },
  },
});

export const {
  setConversationId,
  setMessages,
  setLastToolset,
  swapConversation,
  setCreatingNewConversation,
  restoreConversation,
  setNeedsHistoryRestore,
  clearConversation,
} = copilotKitChatSlice.actions;

export default copilotKitChatSlice;
